import axios from "axios";
import { getAccessToken, setAccessToken, clearAccessToken } from "../utils/tokenService";
import { store, authActions } from "../store";
import { errorPopUp } from "../helpers/errorPopUp";

export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }
  if (typeof window !== "undefined" && window.location && window.location.hostname) {
    const hostname = window.location.hostname;
    if (hostname === "localhost" && hostname === "127.0.0.1") {
      return "http://localhost:9000/api";
    }
  }
  return "https://fit-hub-backend-hqed.onrender.com/api";
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  const performRefresh = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const res = await axios.post(
        `${baseUrl}/auth/refreshToken`,
        {},
        { withCredentials: true }
      );
      const token = res.data.accessToken;
      setAccessToken(token);
      return token;
    } catch (err) {
      // Only force logout if the server explicitly rejected the token (401 or 403)
      // Do NOT log out on network drop, screen sleep, or connection timeout
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        store.dispatch(authActions.setUser({}));
        store.dispatch(authActions.logout());
        clearAccessToken();
      }
      throw err;
    } finally {
      refreshPromise = null;
    }
  };

  refreshPromise = performRefresh();
  return refreshPromise;
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Async request interceptor to inject tokens and handle startup auto-refresh
api.interceptors.request.use(
  async (config) => {
    let token = getAccessToken();

    // If no token in memory but isLoggedIn is true in localStorage, refresh first
    const isRefreshEndpoint = config.url && config.url.includes("/auth/refreshToken");
    if (!token && localStorage.getItem("isLoggedIn") === "true" && !isRefreshEndpoint) {
      try {
        token = await refreshAccessToken();
      } catch (err) {
        console.error("Auto refresh on startup request failed:", err);
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for 401 retries and global 500/network error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Avoid retrying on auth routes like login or register
    const url = originalRequest.url || "";
    const isAuthRoute = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refreshToken");

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute && localStorage.getItem("isLoggedIn") === "true") {
      originalRequest._retry = true;
      try {
        const token = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Global error handler for 5xx and network errors
    const status = error.response?.status;
    const message = error.response?.data?.error?.message || error.message || "Something went wrong";

    if (!status || status >= 500) {
      errorPopUp(message);
    }

    return Promise.reject(error);
  }
);

export default api;
